import { Injectable, InjectionToken, inject, signal } from '@angular/core';
import { ORG_ID, vismaCallId, vismaSessionId } from '@data-access/bulk-operations-api';
import {
  EMPTY,
  Observable,
  Subject,
  Subscription,
  catchError,
  filter,
  map,
  retry,
  single,
  take,
  tap,
  timeout,
  timer,
} from 'rxjs';
import { WebSocketSubject, webSocket } from 'rxjs/webSocket';
import { v4 as uuid_v4 } from 'uuid';

import {
  WsClientMessageDto,
  WsClientPayloadEnum,
  WsClientPayloadSchema,
} from '../models/ws/ws-client-message.dto';
import {
  WsServerMessageDto,
  WsServerPayloadEnum,
  WsServerPayloadSchema,
} from '../models/ws/ws-server-message.dto';

export const WS_BASE_URL = new InjectionToken<string>('WS_BASE_URL');

@Injectable({
  providedIn: 'root',
})
export class WebSocketClient {
  private readonly wsBaseUrl = inject(WS_BASE_URL);
  private readonly orgId = inject(ORG_ID);

  private socketSubject?: WebSocketSubject<WsServerMessageDto | WsClientMessageDto>;
  private socketSubscription?: Subscription;

  private pingSubscription?: Subscription;
  private reconnectSubscription?: Subscription;

  private readonly outerStreamSubject: Subject<WsServerMessageDto> = new Subject();
  private readonly outerStream$ = this.outerStreamSubject.asObservable();

  public readonly isLoggingEnabled = signal<boolean>(false);

  constructor() {
    this.initNewConnection();
    this.monitorConnection();
  }

  getDirectStream(): Observable<WsServerMessageDto> {
    return this.outerStream$;
  }

  getFilteredStream(...payloadTypes: WsServerPayloadEnum[]): Observable<WsServerPayloadSchema> {
    let observable = this.outerStream$;

    if (payloadTypes.length >= 0) {
      observable = this.outerStream$.pipe(
        catchError(() => EMPTY),
        filter((message: WsServerMessageDto) => payloadTypes.includes(message.payload.type)),
      );
    }

    return observable.pipe(map((message) => message.payload));
  }

  getMessagesByType<T extends WsServerPayloadEnum>(
    payloadType: T,
  ): Observable<WsServerPayloadTypeToValueType<T>> {
    let observable = this.outerStream$;

    observable = this.outerStream$.pipe(
      catchError(() => EMPTY),
      filter((message: WsServerMessageDto) => message.payload.type === payloadType),
    );

    return observable.pipe(
      map((message) => message.payload.value as WsServerPayloadTypeToValueType<T>),
    );
  }

  private closeConnection(): void {
    this.socketSubject?.complete();
    this.socketSubscription?.unsubscribe();
  }

  /*
   * Initiates a new connection to the WebSocket server.
   * Method will try to reconnect (up to 3 times in a row) in case of an error.
   */
  private initNewConnection() {
    const newWebSocket = webSocket<WsServerMessageDto | WsClientMessageDto>({
      url:
        `${this.wsBaseUrl}/messages` +
        `?org=${this.orgId}` +
        `&session=${vismaSessionId()}` +
        `&call=${vismaCallId()}`,
      protocol: 'messages',
      openObserver: {
        next: () => {
          // Close existing connection before opening a new one
          this.closeConnection();

          // Save references to the new connection
          this.socketSubject = newWebSocket;
          this.socketSubscription = newSocketSubscription;

          // Keep connection alive
          this.startPingTimer();
          this.startReconnectTimer();

          // Ensure connection is working and we are receiving messages
          this.waitForConnectionEstablishedOrReconnect();
        },
      },
    });

    const socketObservable = newWebSocket.pipe(
      retry({
        count: 3,
        resetOnSuccess: true,
        delay: (err, count) => timer(count * 1000),
      }),
      tap((message) => {
        if (this.isLoggingEnabled()) {
          console.log(message.payload.type, message.payload.value);
        }
      }),
      catchError((err) => {
        console.error('WS: Error', err);
        throw err;
      }),
    );

    const newSocketSubscription = socketObservable.subscribe({
      next: (result) => this.outerStreamSubject.next(result as WsServerMessageDto),
      error: (err) => this.outerStreamSubject.error(err),
    });
  }

  private send(payload: WsClientPayloadSchema): void {
    this.socketSubject?.next({
      messageId: uuid_v4(),
      createdTimeIso: new Date().toISOString(),
      payload,
    });
  }

  private startPingTimer() {
    if (this.pingSubscription) {
      this.pingSubscription.unsubscribe();
    }

    // AWS expects to see socket activity every 10 mins. We do it every 2 mins.
    const pingInterval = 2 * 60 * 1000;

    this.pingSubscription = timer(pingInterval, pingInterval).subscribe(() => {
      this.send({
        type: WsClientPayloadEnum.Ping,
        value: { echoMessage: '¯\\_(ツ)_/¯' },
      });
      this.waitForPongOrReconnect();
    });
  }

  private waitForConnectionEstablishedOrReconnect(): void {
    const timeoutMs = 5000;

    this.getMessagesByType(WsServerPayloadEnum.ConnectionEstablished)
      .pipe(
        filter((msg) => msg.isThisConnection),
        take(1),
        timeout(timeoutMs),
      )
      .subscribe({
        error: (err) => {
          if (err.name === 'TimeoutError') {
            console.warn(
              `WS: ${WsServerPayloadEnum.ConnectionEstablished} not received within ${timeoutMs}ms`,
            );
            this.initNewConnection();
          }
        },
      });
  }

  private waitForPongOrReconnect(): void {
    const timeoutMs = 5000;
    this.getMessagesByType(WsServerPayloadEnum.Pong)
      .pipe(take(1), timeout(timeoutMs))
      .subscribe({
        error: (err) => {
          if (err.name === 'TimeoutError') {
            console.warn(`WS: ${WsServerPayloadEnum.Pong} not received within ${timeoutMs}ms`);
            this.initNewConnection();
          }
        },
      });
  }

  private startReconnectTimer() {
    if (this.reconnectSubscription) {
      this.reconnectSubscription.unsubscribe();
    }

    // AWS has a 2-hour hard limit for socket connection. JS background processing is fiddly so we set a timer by time.
    const currentDate = new Date();
    const startOfNextMinute = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate(),
      currentDate.getHours() + 1,
      currentDate.getMinutes() + 55,
      currentDate.getSeconds(),
    );

    this.reconnectSubscription = timer(startOfNextMinute)
      .pipe(single())
      .subscribe(() => this.initNewConnection());
  }

  private monitorConnection() {
    this.getMessagesByType(WsServerPayloadEnum.ConnectionExpires).subscribe(() => {
      this.initNewConnection();
    });

    this.getMessagesByType(WsServerPayloadEnum.ClientFailure).subscribe((msg) => {
      console.error('WS: Client failure.', msg);
    });
  }
}

export type WsServerPayloadTypeToValueType<T extends WsServerPayloadSchema['type']> =
  T extends WsServerPayloadSchema['type']
    ? Extract<WsServerPayloadSchema, { type: T }>['value']
    : never;
